/**
 * Phase 5 FE — Study Admin AE Configuration panel (AC2).
 *
 * Compact card on the Study Workspace Hub summarising the current AE
 * config (grading scale + option counts) with a "Configure" button
 * that opens a modal. The modal picks a preset (descriptive vs CTCAE v5
 * vs custom) and lets the admin edit the grading options,
 * relationship options, and SAE categories.
 *
 * Write is gated on site_admin / regulatory_specialist / platform
 * (matches the BE handler); read is open to everyone who can view the
 * study.
 */
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, Cog, Loader2, Plus, Trash2, X } from 'lucide-react';
import {
  studyAeConfig,
  type AeOption,
  type StudyAeConfigResponse,
} from '@/api/client';
import { useMyPermissions } from '@/hooks/useMyPermissions';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { UnsavedChangesModal } from '@/components/ui/UnsavedChangesModal';
import { errorText } from '@/lib/permissionError';

const WRITE_ROLES = [
  'regulatory_specialist',
] as const;

const SCALE_LABELS: Record<'descriptive' | 'ctcae_v5' | 'other', string> = {
  descriptive: 'Descriptive (Mild / Moderate / Severe / Life-threatening / Death)',
  ctcae_v5: 'CTCAE v5 (Grades 1–5)',
  other: 'Custom',
};

const DESCRIPTIVE_PRESET: AeOption[] = [
  { value: 'mild', label: 'Mild' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'severe', label: 'Severe' },
  { value: 'life_threatening', label: 'Life-threatening' },
  { value: 'death', label: 'Death' },
];

const CTCAE_V5_PRESET: AeOption[] = [
  { value: '1', label: 'Grade 1 (Mild)' },
  { value: '2', label: 'Grade 2 (Moderate)' },
  { value: '3', label: 'Grade 3 (Severe)' },
  { value: '4', label: 'Grade 4 (Life-threatening)' },
  { value: '5', label: 'Grade 5 (Death)' },
];

export function AdverseEventConfigPanel({ studyId, readOnly = false }: { studyId: string; readOnly?: boolean }) {
  // The grid decides; WRITE_ROLES is the fallback before it loads. See the
  // same note in DoaResponsibilitiesPanel.
  const perms = useMyPermissions();
  const canWrite =
    perms.holdsOperation('studies.adverse_events.config', WRITE_ROLES) && !readOnly;
  const [modalOpen, setModalOpen] = useState(false);

  const q = useQuery({
    queryKey: ['study', studyId, 'ae-config'],
    queryFn: () => studyAeConfig.get(studyId),
    enabled: !!studyId,
  });

  const cfg = q.data?.config;

  return (
    <section className="card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Cog className="h-4 w-4 text-brand-500" />
          <div>
            <h2 className="text-sm font-semibold text-ink-primary">Adverse event configuration</h2>
            <p className="text-xs text-ink-muted">
              Grading scale + options + SAE categories for AEs logged on this study.
            </p>
          </div>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            disabled={q.isLoading}
            className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50 disabled:opacity-50"
          >
            Configure
          </button>
        )}
      </div>
      <div className="mt-3 text-xs text-ink-secondary">
        {q.isLoading ? (
          <span className="flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</span>
        ) : cfg ? (
          <div className="space-y-0.5">
            <div>Scale: <strong>{SCALE_LABELS[cfg.grading_scale_key]}</strong></div>
            <div>
              {cfg.grading_options.length} grading option{cfg.grading_options.length === 1 ? '' : 's'} ·{' '}
              {cfg.relationship_options.length} relationship option{cfg.relationship_options.length === 1 ? '' : 's'}
              {cfg.sae_categories && ` · ${cfg.sae_categories.length} SAE categor${cfg.sae_categories.length === 1 ? 'y' : 'ies'}`}
            </div>
          </div>
        ) : (
          '—'
        )}
      </div>

      {modalOpen && cfg && (
        <ConfigureModal
          studyId={studyId}
          initial={q.data as StudyAeConfigResponse}
          onClose={() => setModalOpen(false)}
        />
      )}
    </section>
  );
}

function ConfigureModal({
  studyId,
  initial,
  onClose,
}: {
  studyId: string;
  initial: StudyAeConfigResponse;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [scale, setScale] = useState<typeof initial.config.grading_scale_key>(initial.config.grading_scale_key);
  const [grading, setGrading] = useState<AeOption[]>(initial.config.grading_options);
  const [relationship, setRelationship] = useState<AeOption[]>(initial.config.relationship_options);
  const [saeCategories, setSaeCategories] = useState<AeOption[] | null>(initial.config.sae_categories);

  const initialSerialised = useMemo(
    () => JSON.stringify({
      scale: initial.config.grading_scale_key,
      grading: initial.config.grading_options,
      relationship: initial.config.relationship_options,
      sae: initial.config.sae_categories,
    }),
    [initial],
  );
  const currentSerialised = JSON.stringify({ scale, grading, relationship, sae: saeCategories });
  const isDirty = currentSerialised !== initialSerialised;
  const unsavedGuard = useUnsavedChangesGuard(isDirty);

  // When the user picks a preset from the scale dropdown, load the
  // corresponding grading options. Switching to 'other' leaves grading
  // untouched so the admin can hand-edit.
  function applyScalePreset(next: typeof scale) {
    setScale(next);
    if (next === 'descriptive') setGrading(DESCRIPTIVE_PRESET);
    else if (next === 'ctcae_v5') setGrading(CTCAE_V5_PRESET);
  }

  const save = useMutation({
    mutationFn: () =>
      studyAeConfig.update(studyId, {
        grading_scale_key: scale,
        grading_options: grading,
        relationship_options: relationship,
        sae_categories: saeCategories,
      }),
    onSuccess: () => {
      toast.success('AE configuration saved.');
      qc.invalidateQueries({ queryKey: ['study', studyId, 'ae-config'] });
      onClose();
    },
    onError: (e) => toast.error(errorText(e, 'Failed to save')),
  });

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 !m-0"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative flex max-h-[92vh] w-full max-w-2xl flex-col rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <header className="flex items-center justify-between border-b border-surface-border px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-ink-primary">Configure AE recording</h3>
            <p className="text-[11px] text-ink-muted">
              Options here populate the AE form's Grade / Relationship / SAE category dropdowns.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted/50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">Grading scale</span>
            <select
              value={scale}
              onChange={(e) => applyScalePreset(e.target.value as typeof scale)}
              className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
            >
              <option value="descriptive">Descriptive (Mild / Moderate / Severe / Life-threatening / Death)</option>
              <option value="ctcae_v5">CTCAE v5 (Grades 1-5)</option>
              <option value="other">Custom</option>
            </select>
            {scale !== 'other' && (
              <p className="mt-1 text-[10px] text-ink-muted">
                Changing the scale loads that preset into the grading options below. Edit further if needed.
              </p>
            )}
          </label>

          <OptionEditor
            label="Grading options"
            options={grading}
            onChange={setGrading}
            emptyMessage="At least one grading option is required."
          />

          <OptionEditor
            label="Relationship options"
            options={relationship}
            onChange={setRelationship}
            emptyMessage="At least one relationship option is required."
          />

          <div className="rounded border border-surface-border p-3">
            <div className="mb-2 flex items-center justify-between">
              <div>
                <div className="text-xs font-medium text-ink-secondary">SAE categories</div>
                <div className="text-[10px] text-ink-muted">
                  Optional — surfaces on the AE form only when Serious=Yes.
                </div>
              </div>
              {saeCategories === null ? (
                <button
                  type="button"
                  onClick={() => setSaeCategories([])}
                  className="rounded border border-surface-border px-2 py-0.5 text-[11px] hover:bg-surface-muted/50"
                >
                  <Plus className="mr-0.5 inline h-3 w-3" /> Enable
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setSaeCategories(null)}
                  className="rounded border border-surface-border px-2 py-0.5 text-[11px] text-danger-700 hover:bg-danger-50"
                >
                  <Trash2 className="mr-0.5 inline h-3 w-3" /> Disable
                </button>
              )}
            </div>
            {saeCategories !== null && (
              <OptionEditor
                label=""
                options={saeCategories}
                onChange={setSaeCategories}
                emptyMessage="Add at least one SAE category, or disable this section."
                compact
              />
            )}
          </div>

          <p className="rounded border border-amber-200 bg-amber-50/60 p-2 text-[11px] text-amber-800">
            <AlertTriangle className="mr-1 inline h-3 w-3" />
            Config changes apply to new AEs going forward. Existing AEs keep the grade / relationship values captured at log time.
          </p>
        </div>

        <footer className="flex justify-end gap-2 border-t border-surface-border px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={!isDirty || save.isPending}
            className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </footer>

        <UnsavedChangesModal guard={unsavedGuard} />
      </div>
    </div>,
    document.body,
  );
}

function OptionEditor({
  label,
  options,
  onChange,
  emptyMessage,
  compact,
}: {
  label: string;
  options: AeOption[];
  onChange: (next: AeOption[]) => void;
  emptyMessage: string;
  compact?: boolean;
}) {
  return (
    <div className={compact ? '' : 'rounded border border-surface-border p-3'}>
      {label && (
        <div className="mb-2 text-xs font-medium text-ink-secondary">{label}</div>
      )}
      {options.length === 0 && (
        <p className="mb-2 text-[11px] text-danger-600">{emptyMessage}</p>
      )}
      <ul className="space-y-1">
        {options.map((opt, i) => (
          <li key={i} className="flex items-center gap-2">
            <input
              type="text"
              value={opt.value}
              onChange={(e) => {
                const next = [...options];
                next[i] = { ...opt, value: e.target.value };
                onChange(next);
              }}
              placeholder="value"
              className="w-32 rounded border border-surface-border px-2 py-1 font-mono text-[11px]"
            />
            <input
              type="text"
              value={opt.label}
              onChange={(e) => {
                const next = [...options];
                next[i] = { ...opt, label: e.target.value };
                onChange(next);
              }}
              placeholder="Display label"
              className="flex-1 rounded border border-surface-border px-2 py-1 text-xs"
            />
            <button
              type="button"
              onClick={() => onChange(options.filter((_, j) => j !== i))}
              className="rounded p-1 text-danger-600 hover:bg-danger-50"
              aria-label="Remove option"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={() => onChange([...options, { value: '', label: '' }])}
        className="mt-2 rounded border border-dashed border-surface-border px-2 py-0.5 text-[11px] text-ink-secondary hover:bg-surface-muted/40"
      >
        <Plus className="mr-0.5 inline h-3 w-3" /> Add option
      </button>
    </div>
  );
}
